import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEW_URL_DEFAULT =
  process.env.REACT_APP_GOOGLE_REVIEW_URL ||
  "https://www.google.com/search?q=Swiss+Premium+Insurance+avis";

// Fallback statique si l'API Google Places n'est pas configurée
const FALLBACK_REVIEWS = [
  { name: "Sandra L.", initials: "SL", date: "il y a 2 semaines", rating: 5,
    text: "Accompagnement exceptionnel pour ma demande de subsides. L'équipe a tout pris en charge et j'ai obtenu mon dossier en moins de 3 semaines." },
  { name: "Olivier P.", initials: "OP", date: "il y a 1 mois", rating: 5,
    text: "Professionnels, disponibles et très clairs. Ils ont identifié des subsides auxquels je ne savais pas avoir droit. Économie de plus de 2'000 CHF cette année." },
  { name: "Famille B.", initials: "FB", date: "il y a 1 mois", rating: 5,
    text: "Service privé d'intermédiation au top. Tout est expliqué simplement et l'équipe est disponible. Nos primes ont baissé sans rien perdre en couverture." },
  { name: "Mehdi A.", initials: "MA", date: "il y a 2 mois", rating: 5,
    text: "Très satisfait du suivi. Réponse rapide, accompagnement personnalisé pour la demande de subside. Je remercie toute l'équipe Swiss Premium Insurance." },
  { name: "Christelle D.", initials: "CD", date: "il y a 2 mois", rating: 4,
    text: "Excellent suivi, équipe à l'écoute. La vérification d'éligibilité a été faite très rapidement et le dossier est passé sans accroc." },
  { name: "Yannick R.", initials: "YR", date: "il y a 3 mois", rating: 5,
    text: "Démarche claire et transparente. On comprend bien que c'est un service privé d'accompagnement et la valeur ajoutée est réelle. Merci !" },
];

const palette = ["#211d42", "#e30713", "#0f766e", "#b45309", "#6d28d9", "#1e40af"];

function initialsOf(name) {
  return (name || "").split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "·";
}

function StarsRow({ n }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < n ? "fill-[#FBBC05] text-[#FBBC05]" : "text-slate-300"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function GoogleLogo({ size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

export default function GoogleReviews() {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);
  const [rating, setRating] = useState(4.9);
  const [total, setTotal] = useState(187);
  const [reviewUrl, setReviewUrl] = useState(GOOGLE_REVIEW_URL_DEFAULT);
  const [isLive, setIsLive] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/google-reviews`);
        if (cancelled) return;
        if (data?.enabled && Array.isArray(data.reviews) && data.reviews.length) {
          setReviews(
            data.reviews.map((r) => ({
              name: r.name,
              initials: initialsOf(r.name),
              photo: r.photo,
              date: r.date,
              rating: r.rating || 5,
              text: r.text || "",
            }))
          );
          if (typeof data.rating === "number") setRating(data.rating);
          if (typeof data.user_ratings_total === "number") setTotal(data.user_ratings_total);
          if (data.google_maps_uri) setReviewUrl(data.google_maps_uri);
          setIsLive(true);
        }
      } catch (e) {
        // silent fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const per = 3;
  const max = Math.max(0, reviews.length - per);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i >= max ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(id);
  }, [max]);

  const visible = reviews.slice(index, index + per);
  while (visible.length < per && reviews.length) {
    visible.push(reviews[(index + visible.length) % reviews.length]);
  }

  return (
    <section
      id="avis"
      data-testid="google-reviews-section"
      data-live={isLive ? "true" : "false"}
      className="py-24 md:py-32 bg-white border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-spi-red mb-3">
              Avis vérifiés Google {isLive && <span className="text-emerald-600">· live</span>}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-spi-blue font-semibold max-w-2xl leading-tight">
              Des clients satisfaits, partout en Suisse.
            </h2>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-5 py-3">
              <GoogleLogo size={22} />
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-xl text-slate-900" data-testid="reviews-rating">
                    {(rating || 0).toFixed(1)}
                  </span>
                  <StarsRow n={Math.round(rating || 0)} />
                </div>
                <a
                  href={reviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate-500 hover:text-spi-blue inline-flex items-center gap-1"
                >
                  Voir les {total || ""} avis <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                data-testid="reviews-prev"
                aria-label="Avis précédent"
                className="w-10 h-10 rounded-full border border-slate-200 hover:border-spi-blue hover:bg-slate-50 inline-flex items-center justify-center"
              >
                <ChevronLeft className="w-4 h-4 text-slate-700" />
              </button>
              <button
                onClick={() => setIndex((i) => Math.min(max, i + 1))}
                data-testid="reviews-next"
                aria-label="Avis suivant"
                className="w-10 h-10 rounded-full border border-slate-200 hover:border-spi-blue hover:bg-slate-50 inline-flex items-center justify-center"
              >
                <ChevronRight className="w-4 h-4 text-slate-700" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {visible.map((r, i) => (
            <article
              key={`${index}-${i}-${r.name}`}
              data-testid={`google-review-${i}`}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow fade-up"
            >
              <div className="flex items-center gap-3">
                {r.photo ? (
                  <img
                    src={r.photo}
                    alt={r.name}
                    referrerPolicy="no-referrer"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="w-10 h-10 rounded-full text-white font-semibold flex items-center justify-center text-sm"
                    style={{ backgroundColor: palette[(index + i) % palette.length] }}
                  >
                    {r.initials || initialsOf(r.name)}
                  </div>
                )}
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.date}</div>
                </div>
                <span className="ml-auto"><GoogleLogo /></span>
              </div>
              <div className="mt-3">
                <StarsRow n={r.rating || 5} />
              </div>
              <p className="mt-3 text-slate-700 text-sm leading-relaxed line-clamp-6">{r.text}</p>
            </article>
          ))}
        </div>

        <div className="mt-8 flex justify-center gap-1.5">
          {Array.from({ length: max + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Aller à l'avis ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "bg-spi-blue w-6" : "bg-slate-300 w-3"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
